class BlogFilters extends HTMLElement {
  constructor() {
    super();
    this.blogHandle = null;
    this.sectionId = null;
    this.abortController = null;
    this.scroller = null;
    this.track = null;
    this.prevButton = null;
    this.nextButton = null;
    this.handleHistoryChange = this.handleHistoryChange.bind(this);
    this.handleScrollerResize = this.handleScrollerResize.bind(this);
    this.handleScrollerScroll = this.handleScrollerScroll.bind(this);

  }

  connectedCallback() {
    this.blogUrl = this.dataset.blogUrl;
    this.events = new AbortController();
    this.bindFilterButtons();
    this.setupScroller();
    this.revealActiveButton('auto');
    window.addEventListener('popstate', this.handleHistoryChange);
    window.addEventListener('resize', this.handleScrollerResize);
  }

  disconnectedCallback() {
    this.events?.abort();
    this.abortController?.abort();
    window.removeEventListener('popstate', this.handleHistoryChange);
    window.removeEventListener('resize', this.handleScrollerResize);

    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.handleScrollerScroll);
    }
  }

  handleHistoryChange() {
    const tag = this.extractTagFromURL();
    this.fetchAndUpdateArticles(tag, false);
  }

  handleScrollerResize() {
    this.syncBreakout();
    this.syncScrollerState();
  }

  handleScrollerScroll() {
    this.syncScrollerState();
  }

  extractTagFromURL() {
    const pathname = window.location.pathname;
    // URL format: /blogs/handle/tagged/tag-name
    const match = pathname.match(/\/tagged\/([^\/]+)/);
    return match ? match[1] : null;
  }

  bindFilterButtons() {
    this.querySelectorAll('.filter-button').forEach((button) => {
      button.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();

        if (button.classList.contains('active')) {
          return;
        }

        const url = button.dataset.url;
        const match = url.match(/\/tagged\/([^?]+)/);
        const tag = match ? match[1] : null;

        this.fetchAndUpdateArticles(tag, true);
      }, { signal: this.events.signal });
    });
  }

  setupScroller() {
    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.handleScrollerScroll);
    }

    this.scroller = this.querySelector('[data-blog-filter-scroller]');
    this.track = this.querySelector('[data-blog-filter-track]');
    this.prevButton = this.querySelector('[data-blog-filter-prev]');
    this.nextButton = this.querySelector('[data-blog-filter-next]');

    if (!this.scroller || !this.track || !this.prevButton || !this.nextButton) {
      return;
    }

    this.prevButton.addEventListener('click', () => this.scrollFilters('prev'), { signal: this.events.signal });
    this.nextButton.addEventListener('click', () => this.scrollFilters('next'), { signal: this.events.signal });
    this.scroller.addEventListener('scroll', this.handleScrollerScroll, { passive: true });
    this.syncBreakout();
    this.syncScrollerState();
  }

  syncBreakout() {
    const parent = this.parentElement;

    if (!parent) {
      return;
    }

    const parentRect = parent.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const breakoutStart = Math.max(0, parentRect.left);
    const breakoutEnd = Math.max(0, viewportWidth - parentRect.right);

    this.style.setProperty('--blog-filter-breakout-start', `${breakoutStart}px`);
    this.style.setProperty('--blog-filter-breakout-end', `${breakoutEnd}px`);
  }

  getScrollTarget(direction) {
    if (!this.scroller) {
      return 0;
    }

    const offsets = [...this.querySelectorAll('.filter-button')].map((button) => button.offsetLeft);
    const firstOffset = offsets[0] ?? 0;
    const normalizedOffsets = offsets.map((offset) => Math.max(0, offset - firstOffset));
    const currentPosition = this.scroller.scrollLeft;

    if (direction === 'prev') {
      const previousOffset = [...normalizedOffsets].reverse().find((offset) => offset < currentPosition - 2);
      return previousOffset ?? 0;
    }

    const nextOffset = normalizedOffsets.find((offset) => offset > currentPosition + 2);
    return nextOffset ?? currentPosition;
  }

  scrollFilters(direction) {
    if (!this.scroller) {
      return;
    }

    this.scroller.scrollTo({
      left: this.getScrollTarget(direction),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  getScrollerInsets() {
    if (!this.scroller) {
      return {
        left: 0,
        right: 0
      };
    }

    const styles = window.getComputedStyle(this.scroller);
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const prevInset = isDesktop && !this.prevButton?.hidden
      ? this.prevButton.parentElement?.getBoundingClientRect().width || 0
      : 0;
    const nextInset = isDesktop && !this.nextButton?.hidden
      ? this.nextButton.parentElement?.getBoundingClientRect().width || 0
      : 0;

    return {
      left: (parseFloat(styles.paddingLeft) || 0) + prevInset,
      right: (parseFloat(styles.paddingRight) || 0) + nextInset
    };
  }

  revealButton(button, behavior = 'smooth') {
    if (!this.scroller || !button) {
      return;
    }

    this.syncScrollerState();

    const scrollerRect = this.scroller.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const { left: leftInset, right: rightInset } = this.getScrollerInsets();
    const visibleLeft = scrollerRect.left + leftInset;
    const visibleRight = scrollerRect.right - rightInset;
    const maxScrollLeft = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);

    let targetScrollLeft = this.scroller.scrollLeft;

    if (buttonRect.left < visibleLeft) {
      targetScrollLeft += buttonRect.left - visibleLeft;
    } else if (buttonRect.right > visibleRight) {
      targetScrollLeft += buttonRect.right - visibleRight;
    }

    targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

    if (Math.abs(targetScrollLeft - this.scroller.scrollLeft) < 1) {
      return;
    }

    this.scroller.scrollTo({
      left: targetScrollLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior
    });
  }

  revealActiveButton(behavior = 'smooth') {
    window.requestAnimationFrame(() => {
      this.revealButton(this.querySelector('.filter-button.active'), behavior);
    });
  }

  syncScrollerState() {
    if (!this.scroller || !this.track || !this.prevButton || !this.nextButton) {
      return;
    }

    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const navOverlayWidth = isDesktop ? 48 : 0;
    const visibleWidth = this.scroller.clientWidth - navOverlayWidth;
    const hasOverflow = isDesktop && this.track.scrollWidth > visibleWidth + 1;
    const canScrollPrev = hasOverflow && this.scroller.scrollLeft > 2;
    const maxScrollLeft = this.scroller.scrollWidth - this.scroller.clientWidth - 2;
    const canScrollNext = hasOverflow && this.scroller.scrollLeft < maxScrollLeft;

    this.classList.toggle('blog-filters--has-overflow', hasOverflow);
    this.classList.toggle('blog-filters--can-scroll-prev', canScrollPrev);
    this.classList.toggle('blog-filters--can-scroll-next', canScrollNext);

    this.prevButton.hidden = !canScrollPrev;
    this.nextButton.hidden = !canScrollNext;
  }

  scrollToFilters() {
    const top = document.querySelector('.main-blog').getBoundingClientRect().top + window.scrollY;
    const headerH = document.querySelector('.shopify-section-group-header-group')?.offsetHeight || 0;
    window.scrollTo({ top: top - headerH, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  async fetchAndUpdateArticles(tag, updateHistory = true) {
    // Cancel any in-flight requests
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Show loading state
    this.showLoadingState();

    try {
      // Get the section ID from the parent section
      const section = this.closest('.shopify-section');
      const sectionId = section ? section.id.replace('shopify-section-', '') : 'template--main';

      // Construct URL - Shopify format: /blogs/handle/tagged/tag-name
      const baseUrl = this.blogUrl || window.location.pathname.split('/tagged')[0];
      const url = new URL(tag ? `${baseUrl}/tagged/${tag}` : baseUrl, window.location.origin);
      if (!updateHistory) url.search = window.location.search;
      url.searchParams.set('section_id', sectionId);

      // Fetch new content
      const response = await fetch(url, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Update blog articles grid and pagination controls
      this.updateArticlesGrid(html);

      if (updateHistory) {
        const newUrl = tag
          ? `${baseUrl}/tagged/${tag}`
          : baseUrl;
        history.pushState({ tag: tag }, '', newUrl);
      }


      const status = this.closest('.shopify-section')?.querySelector('[data-pagination-status]');
      if (status) status.textContent = 'Articles updated.';

      // Update active button state and reorder
      this.updateActiveButton(tag);

      // Scroll to blog
      this.scrollToFilters();

      // Hide loading state
      this.hideLoadingState();

    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, do nothing
        return;
      }

      console.error('Error fetching articles:', error);
      const fallback = updateHistory
        ? (tag ? `${this.blogUrl}/tagged/${tag}` : this.blogUrl)
        : window.location.href;
      window.location.assign(fallback);
      this.hideLoadingState();
    }
  }

  updateArticlesGrid(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const currentSection = this.closest('.shopify-section');
    const newInfiniteScroll = doc.querySelector('.blog-articles-wrapper');
    const currentInfiniteScroll = currentSection?.querySelector('.blog-articles-wrapper');

    if (!newInfiniteScroll || !currentInfiniteScroll) throw new Error('Filter response has no article results');
    if (newInfiniteScroll && currentInfiniteScroll) {
      const replacementWrapper = document.createElement('div');
      replacementWrapper.innerHTML = newInfiniteScroll.outerHTML;
      currentInfiniteScroll.replaceWith(replacementWrapper.firstElementChild);

      // Re-trigger scroll animations if the function exists
      if (typeof initializeScrollAnimationTrigger === 'function') {
        const updatedArticles = currentSection?.querySelector('.blog-articles');

        if (updatedArticles) {
          initializeScrollAnimationTrigger(updatedArticles);
        }
      }
    }
  }

  updateActiveButton(selectedTag) {
    // Remove all active states
    this.querySelectorAll('.filter-button').forEach(button => {
      button.classList.remove('active');
      button.removeAttribute('aria-current');
    });

    const defaultButton = Array.from(this.querySelectorAll('.filter-button')).find(button => {
      return !button.dataset.url.includes('/tagged/');
    });

    // Find and activate the correct button
    let activeButton;
    if (selectedTag) {
      // Find button with matching tag in URL
      activeButton = Array.from(this.querySelectorAll('.filter-button')).find(button => {
        const match = button.dataset.url.match(/\/tagged\/([^?]+)/);
        return match && match[1] === selectedTag;
      });
    } else {
      // Activate "All" button (first button without /tagged/ in URL)
      activeButton = Array.from(this.querySelectorAll('.filter-button')).find(button => {
        return !button.dataset.url.includes('/tagged/');
      });
    }

    if (activeButton) {
      activeButton.classList.add('active');
      activeButton.setAttribute('aria-current', 'page');
    }

    if (defaultButton) {
      const parent = defaultButton.parentElement;
      if (parent) {
        parent.insertBefore(defaultButton, parent.firstChild);
      }
    }

    this.syncScrollerState();
    this.revealButton(activeButton);
  }

  showLoadingState() {
    this.classList.add('loading');
    const articlesWrapper = this.closest('.shopify-section')?.querySelector('.blog-articles-wrapper');
    if (articlesWrapper) {
      articlesWrapper.classList.add('loading');
      articlesWrapper.setAttribute('aria-busy', 'true');
    }
  }

  hideLoadingState() {
    this.classList.remove('loading');
    const articlesWrapper = this.closest('.shopify-section')?.querySelector('.blog-articles-wrapper');
    if (articlesWrapper) {
      articlesWrapper.classList.remove('loading');
      articlesWrapper.removeAttribute('aria-busy');
    }
  }


}

if (!customElements.get('blog-filters')) customElements.define('blog-filters', BlogFilters);
